import { useLazyQuery, useMutation } from "@apollo/client/react";
import moment from 'moment-timezone';
import { useSnackbar } from 'notistack';
import React, { createContext, ReactNode, useCallback, useContext, useEffect, useState } from 'react';
import { useLocation } from 'react-router';
import { Appointment } from 'src/@types/common.types';
import { SESSION_LIVE_SESSION } from 'src/graphql/resolvers/mutations';
import useDeviceDetection from 'src/hooks/useDeviceDetection';
import { PATH_MAIN } from 'src/routes/paths';
import { AssessmentType, VideoCallBuild } from '../enums/shared.enums';
import * as Sentry from '@sentry/react';
import { GET_TEST_SESSION_TOKEN } from 'src/graphql/resolvers/queries';

interface VideoCallContextType {
    isInCall: boolean;
    pipMode: boolean;
    showAssessmentPreview: boolean;
    pendingAssessments: AssessmentType[];
    endCall: () => void;
    togglePipMode: () => void;
    setPendingAssessments: (assessments: AssessmentType[]) => void;
    setShowAssessmentPreview: (show: boolean) => void;
    shouldShowAssessmentPreview: (startDateTime: string) => boolean;

    videoToken: string | null;
    setVideoToken: (token: string | null) => void;
    videoCallBuild: VideoCallBuild | null;
    setVideoCallBuild: (build: VideoCallBuild | null) => void;

    inLiveSession: boolean;
    isSessionLoading: boolean;

    startSession: (appt: Appointment) => void;
    startTestSession: () => void;
    displayName: string;
    joinTimestamp: number;
}

const VideoCallContext = createContext<VideoCallContextType | undefined>(undefined);

export const VideoCallProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [isInCall, setIsInCall] = useState<boolean>(false);
    const [pipMode, setPipMode] = useState<boolean>(false);
    const [showAssessmentPreview, setShowAssessmentPreview] = useState<boolean>(false);
    const [pendingAssessments, setPendingAssessments] = useState<AssessmentType[]>([]);

    const [videoToken, setVideoToken] = useState<string | null>(null);
    const [videoCallBuild, setVideoCallBuild] = useState<VideoCallBuild | null>(
        VideoCallBuild.WHEREBY
    );

    const [displayName, setDisplayName] = useState('');
    const [joinTimestamp, setJoinTimestamp] = useState<number>(0);

    const location = useLocation();

    const { enqueueSnackbar } = useSnackbar();
    const { os } = useDeviceDetection();

    const [startSession, { loading }] = useMutation(SESSION_LIVE_SESSION, {
        onError: (error) => {
            enqueueSnackbar(error.message, { variant: 'error' });
        },
        onCompleted(data) {
            const { token, videoCallBuild } = data.startLiveSession;
            const displayName = data.startLiveSession?.displayName;

            if (!!displayName) {
                setDisplayName(displayName);
            }

            Sentry.setTag('Reachlink_VideoCallBuild', videoCallBuild);
            setVideoToken(token);
            setVideoCallBuild(videoCallBuild);
            setJoinTimestamp(Date.now());
            setIsInCall(true);
        },
    });

    const startSessionCBK = useCallback(
        (appointment: Appointment) => {
            void startSession({
                variables: {
                    appointmentId: appointment?.appointmentId,
                    platform: os,
                },
            });
        },
        [os, startSession]
    );

    const [
        startTestSession,
        { data: testSessionData, error: testSessionError, loading: testSessionTokenLoading },
    ] = useLazyQuery(GET_TEST_SESSION_TOKEN);

    useEffect(() => {
        if (testSessionData) {
            const { token } = testSessionData.getTestSessionToken;
            Sentry.setTag('Reachlink_VideoCallBuild', `${VideoCallBuild.WHEREBY} - TEST Session`);
            setVideoToken(token);
            setVideoCallBuild(VideoCallBuild.WHEREBY);
            setJoinTimestamp(Date.now());
            setIsInCall(true);
        }
    }, [testSessionData]);

    useEffect(() => {
        if (testSessionError) {
            enqueueSnackbar(testSessionError.message, { variant: 'error' });
        }
    }, [testSessionError]);
    const startTestSessionCBK = useCallback(
        () => {
            void startTestSession();
        }, [startTestSession]
    );

    const endCall = useCallback(() => {
        setIsInCall(false);
        setPipMode(false);
        setVideoToken(null);
        setVideoCallBuild(null);
        setJoinTimestamp(0);
    }, []);

    const togglePipMode = useCallback(() => {
        setPipMode((prev) => !prev);
    }, []);

    // Check if assessment preview should be shown based on appointment start time
    // If session started 30+ minutes ago, show assessment preview
    const shouldShowAssessmentPreview = useCallback(
        (startDateTime: string): boolean => {
            const thirtyMinutesAgo = moment().subtract(30, 'minutes');
            const sessionStartTime = moment(startDateTime);

            return sessionStartTime.isBefore(thirtyMinutesAgo);
        },
        [pendingAssessments]
    );

    return (
        <VideoCallContext.Provider
            value={{
                isInCall,
                pipMode,
                showAssessmentPreview,
                pendingAssessments,
                endCall,
                togglePipMode,
                setPendingAssessments,
                setShowAssessmentPreview,
                shouldShowAssessmentPreview,
                inLiveSession: location.pathname === PATH_MAIN.general.liveSession,
                videoCallBuild,
                setVideoCallBuild,
                videoToken,
                setVideoToken,
                isSessionLoading: loading || testSessionTokenLoading,
                startSession: startSessionCBK,
                startTestSession: startTestSessionCBK,
                displayName,
                joinTimestamp,
            }}
        >
            {children}
        </VideoCallContext.Provider>
    );
};

export const useVideoCall = (): VideoCallContextType => {
    const context = useContext(VideoCallContext);
    if (!context) {
        throw new Error('useVideoCall must be used within a VideoCallProvider');
    }
    return context;
};
